import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  isHarbourPupilRecord,
  hfpInstrumentForRecord,
  hasStoredHfpInstrument,
} from '../utils/referralStatus';
import { useParams, useNavigate } from 'react-router-dom';
import { db, functions } from '../../firebase';
import { doc, onSnapshot } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { useAuth } from '../../hooks/useAuth';
import HfpV2Report from '../components/hfp/HfpV2Report';
import buildHfpV3ReportModel from '../utils/hfpV3ReportAdapter';
import HfpReportApp from '../components/hfpReport/HfpReportApp';
import { resolvePronounSet } from '../components/hfpReport/pronouns';

/**
 * RmsServiceUserHfpPage
 * ---------------------
 * Service-user (parent/guardian + school) view of the Holistic Functional
 * Profile at /rms/patient/:patientRecordId/hfp.
 *
 * Records scored under HFP v3 (TD-200) render the redesigned report experience
 * (HfpReportApp — Figma "RMS · Holistic Functional Profile", READY FOR DEV
 * 8 Sep 2026) from the persisted `domains.hfpV3.scoring` via the shared adapter,
 * merging the AI narrative paragraphs + suggested adjustments when the
 * rmsGenerateHfpV3Report callable returns. Records still on v2 fall back to the
 * rmsGenerateHfpV2Profile callable and the legacy HfpV2Report renderer, so
 * legacy referrals keep working.
 */
export default function RmsServiceUserHfpPage() {
  const navigate = useNavigate();
  const { patientRecordId } = useParams();
  const { claims } = useAuth();
  const orgId = claims?.rmsOrgId;

  const [childName, setChildName] = useState('the child');
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // HFP v3: records scored under v3 render the deterministic family report from
  // the persisted payload; the v2 callable only runs for pre-v3 records.
  const [recordLoaded, setRecordLoaded] = useState(false);
  const [harbourPupil, setHarbourPupil] = useState(false);
  const [hfpV3Scoring, setHfpV3Scoring] = useState(null);
  // The record's HFP instrument (TDI-414) and whether it is stored on the
  // record; records created before the setting resolve it from the old rule.
  const [instrument, setInstrument] = useState('v2');
  const [instrumentStored, setInstrumentStored] = useState(false);

  // The child's pronoun set drives the static copy of the redesigned report
  // (the AI narratives arrive personalised from the backend with the same set).
  // A sequence counter keeps overlapping async resolutions ordered (a stale
  // slow lookup must never overwrite a newer result), and once resolved the
  // guardian-form fallback is not refetched on later snapshots.
  const [pronounSet, setPronounSet] = useState('they/them');
  const pronounSeqRef = useRef(0);
  const pronounResolvedRef = useRef(false);

  // Keep the child's first name in sync and detect v3 scoring on the record.
  useEffect(() => {
    if (!orgId || !patientRecordId) return undefined;
    const ref = doc(db, 'rms_organisations', orgId, 'patientRecords', patientRecordId);
    let cancelled = false;
    const unsub = onSnapshot(ref, (snap) => {
      const data = snap.data() || {};
      const first = (data?.subject?.firstName || '').trim();
      if (first) setChildName(first);
      setHfpV3Scoring(data?.domains?.hfpV3?.scoring || null);
      setHarbourPupil(isHarbourPupilRecord(data));
      setInstrument(hfpInstrumentForRecord(data));
      setInstrumentStored(hasStoredHfpInstrument(data));
      setRecordLoaded(true);
      const hasRecordPronouns = Boolean(data?.subject?.preferredPronouns || data?.subject?.pronouns);
      if (hasRecordPronouns || !pronounResolvedRef.current) {
        const seq = ++pronounSeqRef.current;
        resolvePronounSet(orgId, patientRecordId, data).then((set) => {
          if (cancelled || seq !== pronounSeqRef.current) return;
          pronounResolvedRef.current = true;
          setPronounSet(set);
        });
      }
    }, () => setRecordLoaded(true));
    return () => {
      cancelled = true;
      unsub();
    };
  }, [orgId, patientRecordId]);

  // v2 fallback: the deterministic + LLM report for records not scored under v3.
  const loadReport = useCallback(async () => {
    if (!orgId || !patientRecordId) return;
    setLoading(true);
    setError(null);
    try {
      const fn = httpsCallable(functions, 'rmsGenerateHfpV2Profile', { timeout: 540000 });
      const res = await fn({ orgId, patientRecordId, mode: 'llm' });
      const content = res?.data?.report || null;
      if (content) setReport(content);
      else if (res?.data?.status === 'not_ready') setError('The profile is not ready yet — it appears once all forms are complete.');
      else setError('No report was returned. Please try again shortly.');
    } catch (err) {
      setError(err?.message || 'Could not load the profile. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [orgId, patientRecordId]);

  // HFP v3 narratives (AI paragraphs + suggested adjustments): the same
  // writer/matcher chain the clinician view uses. The deterministic report
  // renders immediately; the paragraphs merge in when the callable returns
  // (cached after the first run for unchanged inputs).
  // narrativesState mirrors the callable's typed statuses:
  //   'loading'   — request in flight (or not yet made)
  //   'ready'     — a narratives payload arrived (generated / cached / with errors)
  //   'not_ready' — the backend reports the report is not ready to generate yet
  //   'error'     — the call failed or returned nothing usable
  const [v3Narratives, setV3Narratives] = useState(null);
  const [v3NarrativesState, setV3NarrativesState] = useState('loading');
  const loadV3Narratives = useCallback(async () => {
    if (!orgId || !patientRecordId) return;
    setV3NarrativesState('loading');
    try {
      const fn = httpsCallable(functions, 'rmsGenerateHfpV3Report', { timeout: 540000 });
      const res = await fn({ orgId, patientRecordId, mode: 'llm' });
      if (res?.data?.narratives) {
        setV3Narratives(res.data.narratives);
        setV3NarrativesState('ready');
      } else if (res?.data?.status === 'not_ready') {
        setV3NarrativesState('not_ready');
      } else {
        setV3NarrativesState('error');
      }
    } catch (err) {
      // Narratives are additive: the deterministic report stands on failure.
      console.error('HFP v3 narratives failed:', err);
      setV3NarrativesState('error');
    }
  }, [orgId, patientRecordId]);

  // Request the narratives once per page view: later record snapshots (status
  // changes, the other informant submitting) must not re-fire the long LLM
  // call or wipe a visible error state mid-read. Retry stays explicit.
  const narrativesRequestedRef = useRef(false);
  // A record with a stored instrument only renders its own generation; records
  // created before the setting render v3 whenever v3 scoring is present.
  const showV3 = Boolean(hfpV3Scoring) && (!instrumentStored || instrument === 'v3');
  useEffect(() => {
    if (!recordLoaded) return;
    if (showV3) {
      setLoading(false);
      if (!narrativesRequestedRef.current) {
        narrativesRequestedRef.current = true;
        loadV3Narratives();
      }
      return;
    }
    // Records on v3 (Harbour pupils by default) or v4 (whose report engine is
    // still to come) are not ready without their own scoring. Never fall back
    // to building a v2 report for them.
    if (instrument !== 'v2') {
      setLoading(false);
      setError('The profile is not ready yet — it appears once all forms are complete.');
      return;
    }
    loadReport();
  }, [recordLoaded, showV3, instrument, loadReport, loadV3Narratives]);

  // v3: the redesigned report experience owns the whole screen (its app bar
  // carries the Back control, and the legal notice rides at the foot of every
  // page inside the app).
  // Back from the profile: Harbour pupils return to the new portal page (the
  // parent's child page, or the school's pupil record); legacy records keep
  // the service-user overview.
  const isGuardianViewer = String(claims?.userType || '').toLowerCase().includes('guardian');
  const backRoute = harbourPupil
    ? (isGuardianViewer
      ? `/rms/family/child/${patientRecordId}`
      : `/rms/pupils/${patientRecordId}`)
    : `/rms/patient/${patientRecordId}/overview`;

  const v3Model = useMemo(
    () => (showV3 ? buildHfpV3ReportModel(hfpV3Scoring, childName, v3Narratives) : null),
    [showV3, hfpV3Scoring, childName, v3Narratives]
  );

  if (showV3) {
    return (
      <div className="h-screen">
        <HfpReportApp
          model={v3Model}
          childName={childName}
          pronounSet={pronounSet}
          narrativesState={v3NarrativesState}
          onRetryNarratives={loadV3Narratives}
          onBack={() => navigate(backRoute)}
        />
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-gradient-to-br from-primary/5 via-white to-secondary/15">
      {/* Slim top bar with a route back to the overview */}
      <div className="flex-shrink-0 flex items-center gap-3 px-4 sm:px-6 py-2 border-b border-gray-200 bg-white/70">
        <button
          type="button"
          onClick={() => navigate(backRoute)}
          className="px-3 py-1.5 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50"
        >
          &larr; Back
        </button>
      </div>

      <div className="flex-1 min-h-0 px-4 sm:px-6 py-4 overflow-hidden">
        <div className="h-full bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden flex flex-col">
          {loading && !report && (
            <div className="flex-1 flex items-center justify-center text-sm text-gray-500 p-8 text-center">
              Preparing the profile — this can take a minute or two the first time.
            </div>
          )}
          {error && !report && (
            <div className="flex-1 flex items-center justify-center text-sm text-red-600 p-8 text-center">{error}</div>
          )}
          {report && (
            <div className="flex-1 min-h-0">
              <HfpV2Report report={report} childName={childName} pageMode />
            </div>
          )}
        </div>
      </div>

      <footer className="flex-shrink-0 border-t border-gray-200 bg-white px-4 sm:px-6 py-3">
        <p className="text-[11px] leading-relaxed text-gray-500 text-center max-w-4xl mx-auto">
          This is not a medical assessment and does not constitute a diagnosis, and cannot be
          used to claim or deny any medical condition.
        </p>
      </footer>
    </div>
  );
}
